import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonProps {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div
      className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%] ${className}`}
      style={{
        animation: 'shimmer 2s ease-in-out infinite',
      }}
    />
  );
};

export const SkeletonCard: React.FC = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
      <div className="flex flex-col items-center gap-3">
        <Skeleton className="w-24 h-24 rounded" />
        <div className="w-full space-y-2">
          <Skeleton className="h-4 w-3/4 mx-auto rounded" />
          <Skeleton className="h-3 w-1/2 mx-auto rounded" />
        </div>
      </div>
    </div>
  );
};

export const SkeletonListItem: React.FC = () => {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <Skeleton className="w-16 h-16 rounded-full flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/3 rounded" />
        <Skeleton className="h-3 w-1/2 rounded" />
        <Skeleton className="h-3 w-3/4 rounded" />
      </div>
    </div>
  );
};

export const SkeletonPatrulhaCard: React.FC = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="w-16 h-20 rounded" />
        <div className="w-full space-y-0.5">
          <Skeleton className="h-3 w-3/4 mx-auto rounded" />
          <Skeleton className="h-2 w-1/2 mx-auto rounded" />
          <Skeleton className="h-2 w-2/3 mx-auto rounded" />
        </div>
        <div className="flex gap-0.5 justify-center pt-1.5 border-t w-full">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      </div>
    </div>
  );
};

export const SkeletonResponsavel: React.FC = () => {
  return (
    <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <Skeleton className="w-24 h-24 rounded flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-1/3 rounded" />
        <Skeleton className="h-4 w-1/4 rounded" />
      </div>
    </div>
  );
};

// Componente com animação de fade-in
interface FadeInWrapperProps {
  children: React.ReactNode;
  delay?: number;
}

export const FadeInWrapper: React.FC<FadeInWrapperProps> = ({ children, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

// Componente com animação de stagger para listas
interface StaggerWrapperProps {
  children: React.ReactNode;
  index: number;
}

export const StaggerWrapper: React.FC<StaggerWrapperProps> = ({ children, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        ease: 'easeOut',
      }}
    >
      {children}
    </motion.div>
  );
};
